import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './CommandPalette.css';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('adminToken')}` });

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ pages: [], products: [], orders: [], customers: [] });
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  // Global hotkey
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 50); }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await fetch(`/api/admin/search?q=${encodeURIComponent(q)}`, { headers: auth() });
        if (r.ok) { setResults(await r.json()); setActive(0); }
      } catch (_) {}
    }, 150);
    return () => clearTimeout(debounceRef.current);
  }, [q, open]);

  const flat = useCallback(() => {
    const out = [];
    for (const p of (results.pages || []))     out.push({ kind: 'page',     ...p });
    for (const p of (results.products || []))  out.push({ kind: 'product',  ...p, href: `/admin/products/${p.id}`, title: p.name, subtitle: `${p.set_name || ''} #${p.card_number || '?'} · $${Number(p.price || 0).toFixed(2)} · stock ${p.stock}` });
    for (const o of (results.orders || []))    out.push({ kind: 'order',    ...o, href: `/admin/orders?id=${o.id}`, title: `Order #${o.id}`, subtitle: `${o.customer_email} · $${Number(o.grand_total || 0).toFixed(2)} · ${o.status}` });
    for (const c of (results.customers || [])) out.push({ kind: 'customer', ...c, href: `/admin/customers/${c.id}`, title: c.email, subtitle: `${c.first_name || ''} ${c.last_name || ''}` });
    return out;
  }, [results]);

  const items = flat();

  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(items.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(0, a - 1)); }
    else if (e.key === 'Enter')   { e.preventDefault(); pick(items[active]); }
  };

  const pick = (item) => {
    if (!item) return;
    setOpen(false); setQ('');
    navigate(item.href);
  };

  if (!open) return null;
  return (
    <div className="cmdk__backdrop" onClick={() => setOpen(false)}>
      <div className="cmdk" onClick={e => e.stopPropagation()}>
        <input
          ref={inputRef}
          className="cmdk__input"
          value={q}
          onChange={e => setQ(e.target.value)}
          onKeyDown={onKey}
          placeholder="Search products, orders, customers, pages…   (⌘K)"
        />
        <ul className="cmdk__list">
          {items.length === 0 && <li className="cmdk__empty">No matches</li>}
          {items.map((it, i) => (
            <li key={`${it.kind}-${it.id || it.href}`}
                className={`cmdk__item ${i === active ? 'cmdk__item--active' : ''}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(it)}>
              <span className={`cmdk__kind cmdk__kind--${it.kind}`}>{ICONS[it.kind] || ''}</span>
              <div className="cmdk__body">
                <div className="cmdk__title">{it.title}</div>
                <small className="cmdk__subtitle">{it.subtitle}</small>
              </div>
            </li>
          ))}
        </ul>
        <footer className="cmdk__footer">
          <span>↑↓ navigate</span><span>↵ open</span><span>esc close</span>
        </footer>
      </div>
    </div>
  );
}
const ICONS = { page: '📄', product: '🎴', order: '📦', customer: '👤' };
